const SECOND_EPOCH = 1000;
const MINUTE_EPOCH = SECOND_EPOCH * 60;
const HOUR_EPOCH = MINUTE_EPOCH * 60;
const DAY_EPOCH = HOUR_EPOCH * 24;
const MONTH_EPOCH = DAY_EPOCH * 30;
const YEAR_EPOCH = DAY_EPOCH * 365;

const getDateFormatOptions = (
    timestamp: number
): { scaledValue: number; unit: 'second' | 'minute' | 'hour' | 'day' | 'week' | 'month' | 'year' } => {
    const compareValueTimestamp = Math.abs(timestamp);
    if (compareValueTimestamp < MINUTE_EPOCH) {
        return { scaledValue: timestamp / SECOND_EPOCH, unit: 'second' };
    }
    if (compareValueTimestamp < HOUR_EPOCH) {
        return { scaledValue: timestamp / MINUTE_EPOCH, unit: 'minute' };
    }
    if (compareValueTimestamp < DAY_EPOCH) {
        return { scaledValue: timestamp / HOUR_EPOCH, unit: 'hour' };
    }
    if (compareValueTimestamp < MONTH_EPOCH) {
        return { scaledValue: timestamp / DAY_EPOCH, unit: 'day' };
    }
    if (compareValueTimestamp < YEAR_EPOCH) {
        return { scaledValue: timestamp / MONTH_EPOCH, unit: 'month' };
    }
    return { scaledValue: timestamp / YEAR_EPOCH, unit: 'year' };
};

export const getTextualRelativeTime = (timeAgoEpochMS: number, locale: string): string => {
    const currentTimeMS = new Date().valueOf();
    const deltaTimeMS = timeAgoEpochMS - currentTimeMS;
    const { scaledValue, unit } = getDateFormatOptions(deltaTimeMS);
    const roundedValue = Math.floor(scaledValue);

    const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto', style: 'narrow' });

    return rtf.format(roundedValue, unit);
};

export const getGMTOffset = (timeZone: string): string => {
    const now = new Date();

    const options = {
        timeZone,
        timeZoneName: 'short',
        hour12: false,
    } as const;

    const formatter = new Intl.DateTimeFormat('en-US', options);
    const parts = formatter.formatToParts(now);

    const tzName = parts.find((p) => p.type === 'timeZoneName')?.value;
    return tzName || 'GMT';
};

const getTimezoneOffsetInHours = (timeZone: string): number => {
    const timeZoneName = getGMTOffset(timeZone);
    const match = timeZoneName.match(/GMT([+-]\d{1,2})/);
    if (!match) {
        return 0;
    }

    return parseInt(match[1], 10);
};

export const diffTimeZonesInHours = (tz1: string, tz2: string): number => {
    const offset1 = getTimezoneOffsetInHours(tz1);
    const offset2 = getTimezoneOffsetInHours(tz2);
    return offset1 - offset2;
};
